package com.merkost.handpass.sharedweb.components

import androidx.compose.runtime.Composable
import com.merkost.handpass.sharedweb.theme.Accent
import com.merkost.handpass.sharedweb.theme.AccentSoft
import com.merkost.handpass.sharedweb.theme.Border
import com.merkost.handpass.sharedweb.theme.Danger
import com.merkost.handpass.sharedweb.theme.DangerSoft
import com.merkost.handpass.sharedweb.theme.Info
import com.merkost.handpass.sharedweb.theme.InfoSoft
import com.merkost.handpass.sharedweb.theme.OnSurfaceFaint
import com.merkost.handpass.sharedweb.theme.OnSurfaceMuted
import com.merkost.handpass.sharedweb.theme.RadiusControl
import com.merkost.handpass.sharedweb.theme.RadiusFull
import com.merkost.handpass.sharedweb.theme.RadiusPill
import com.merkost.handpass.sharedweb.theme.Success
import com.merkost.handpass.sharedweb.theme.SuccessSoft
import com.merkost.handpass.sharedweb.theme.SurfaceMuted
import com.merkost.handpass.sharedweb.theme.Transparent
import com.merkost.handpass.sharedweb.theme.TypeBadge
import com.merkost.handpass.sharedweb.theme.TypePill
import com.merkost.handpass.sharedweb.theme.TypeXs
import com.merkost.handpass.sharedweb.theme.Warning
import com.merkost.handpass.sharedweb.theme.WarningSoft
import com.merkost.handpass.sharedweb.theme.WebSize
import com.merkost.handpass.sharedweb.theme.transitionColors
import com.varabyte.kobweb.compose.css.CSSLengthNumericValue
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.alignItems
import com.varabyte.kobweb.compose.ui.modifiers.display
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.toAttrs
import org.jetbrains.compose.web.css.AlignItems
import org.jetbrains.compose.web.css.CSSColorValue
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.backgroundColor
import org.jetbrains.compose.web.css.borderRadius
import org.jetbrains.compose.web.css.color
import org.jetbrains.compose.web.css.fontSize
import org.jetbrains.compose.web.css.height
import org.jetbrains.compose.web.css.padding
import org.jetbrains.compose.web.css.width
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

enum class BadgeTone { Neutral, Accent, Success, Warning, Danger, Info }
enum class BadgeDensity { Default, Compact, Tight }
enum class BadgeEmphasis { Soft, Plain }
enum class BadgeShape { Pill, Tag }

private data class BadgeColors(val fg: CSSColorValue, val bg: CSSColorValue, val dot: CSSColorValue)

private data class BadgeMetrics(
    val padV: CSSLengthNumericValue,
    val padH: CSSLengthNumericValue,
    val dot: CSSLengthNumericValue,
    val font: CSSLengthNumericValue,
    val lineHeight: Double,
)

private fun BadgeDensity.metrics(): BadgeMetrics = when (this) {
    BadgeDensity.Default -> BadgeMetrics(
        WebSize.P3.value(),
        WebSize.P10.value(),
        WebSize.P6.value(),
        TypeBadge.value(),
        1.4,
    )
    BadgeDensity.Compact -> BadgeMetrics(
        WebSize.P2.value(),
        WebSize.P8.value(),
        WebSize.P5.value(),
        TypePill.value(),
        1.3,
    )
    BadgeDensity.Tight -> BadgeMetrics(
        WebSize.P3.value(),
        WebSize.P8.value(),
        WebSize.P5.value(),
        TypeXs.value(),
        1.2,
    )
}

private fun BadgeTone.colors(): BadgeColors = when (this) {
    BadgeTone.Neutral -> BadgeColors(OnSurfaceMuted.value(), SurfaceMuted.value(), OnSurfaceFaint.value())
    BadgeTone.Accent -> BadgeColors(Accent.value(), AccentSoft.value(), Accent.value())
    BadgeTone.Success -> BadgeColors(Success.value(), SuccessSoft.value(), Success.value())
    BadgeTone.Warning -> BadgeColors(Warning.value(), WarningSoft.value(), Warning.value())
    BadgeTone.Danger -> BadgeColors(Danger.value(), DangerSoft.value(), Danger.value())
    BadgeTone.Info -> BadgeColors(Info.value(), InfoSoft.value(), Info.value())
}

val WebBadgeStyle = CssStyle.base {
    Modifier
        .display(DisplayStyle.LegacyInlineFlex)
        .alignItems(AlignItems.Center)
        .gap(WebSize.P6.value())
        .fontWeight(600)
        .transitionColors()
        .styleModifier {
            property("width", "fit-content")
            property("max-width", "100%")
            property("white-space", "nowrap")
            property("letter-spacing", "0")
        }
}

@Composable
fun WebBadge(
    label: String,
    tone: BadgeTone = BadgeTone.Neutral,
    density: BadgeDensity = BadgeDensity.Default,
    emphasis: BadgeEmphasis = BadgeEmphasis.Soft,
    shape: BadgeShape = BadgeShape.Pill,
    showDot: Boolean = true,
) {
    val colors = tone.colors()
    val metrics = density.metrics()
    Span(
        attrs = WebBadgeStyle.toAttrs {
            style {
                color(colors.fg)
                backgroundColor(if (emphasis == BadgeEmphasis.Soft) colors.bg else Transparent.value())
                if (emphasis == BadgeEmphasis.Plain) {
                    property("border", "${WebSize.P1.value()} solid ${Border.value()}")
                }
                borderRadius(if (shape == BadgeShape.Pill) RadiusPill.value() else RadiusControl.value())
                padding(metrics.padV, metrics.padH)
                fontSize(metrics.font)
                property("line-height", metrics.lineHeight.toString())
            }
        }
    ) {
        if (showDot) {
            Span(
                attrs = {
                    style {
                        width(metrics.dot)
                        height(metrics.dot)
                        borderRadius(RadiusFull.value())
                        backgroundColor(colors.dot)
                        property("flex-shrink", "0")
                    }
                }
            ) {}
        }
        Text(label)
    }
}
